import {
  addDoc,
  collection,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import {
  getFirebaseApp,
  getFirebaseFirestore,
  getFunctionsRegion,
} from "./firebase";
import { getCurrentUser } from "./auth";
import { captureException, track } from "./telemetry";
import type { PremiumFeatureKey } from "./premium";

/**
 * Thin client over the Firebase Stripe extension
 * (`invertase/firestore-stripe-payments`). Three responsibilities:
 *
 *   - {@link fetchPremiumPrices}: read the synced product/price catalog
 *     from Firestore so the upsell overlay can render the live price.
 *   - {@link startPremiumCheckout}: create a `checkout_sessions` doc, wait
 *     for the extension to attach the Stripe Checkout URL, then redirect.
 *   - {@link openCustomerPortal}: call the extension's `createPortalLink`
 *     callable so subscribers can update their payment method.
 *   - {@link cancelPremiumSubscription}: schedule cancellation at the
 *     end of the current billing period (or undo it) so metered overage
 *     still lands on the final invoice.
 *
 * The extension owns every Stripe API key — nothing secret lives in the
 * client bundle. All callers must be signed in; checkout sessions are
 * scoped to `customers/{uid}` by Firestore rules.
 */

const PORTAL_CALLABLE_NAME = "ext-firestore-stripe-payments-createPortalLink";
const CANCEL_CALLABLE_NAME = "cancelPremiumSubscription";

const MANAGEABLE_SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due"] as const;

export type ManageableSubscriptionStatus =
  (typeof MANAGEABLE_SUBSCRIPTION_STATUSES)[number];

export type CancelSubscriptionAction = "schedule" | "resume";

/** Mirrors `CancelSubscriptionResult` in `functions/src/billing/cancelSubscription.ts`. */
export interface CancelSubscriptionResult {
  cancelAtPeriodEnd: boolean;
  /** Unix seconds, or null when the subscription doc has no period end. */
  currentPeriodEnd: number | null;
  status: string;
}

export interface ManageableSubscription {
  id: string;
  status: ManageableSubscriptionStatus;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: number | null;
}

/** A subscription price the upsell overlay can render. Field names mirror
 *  the Stripe data model the extension syncs into Firestore. */
export interface PremiumPrice {
  id: string;
  productId: string;
  productName: string;
  productDescription: string | null;
  /** Amount in the smallest currency unit (e.g. cents). */
  unitAmount: number;
  /** ISO 4217 currency code (lowercased, the way Stripe stores it). */
  currency: string;
  /** Billing interval. `null` for one-time prices (the extension supports
   *  both, but Premium is always recurring). */
  interval: "day" | "week" | "month" | "year" | null;
  trialPeriodDays: number | null;
  /** Whether this Price is the base flat fee (`"base"`), the metered
   *  overage component (`"metered"`), or pre-migration single-Price
   *  setup (`null`). Tagged via Stripe metadata `role` on each Price.
   *  Lets the upsell overlay show the flat-fee number prominently
   *  while still wiring both into the Checkout Session. */
  role: PremiumPriceRole | null;
}

/** Stripe metadata tag we expect on each Price under the Premium
 *  product. The DASHBOARD setup adds these tags so the client can pick
 *  the right pair without hardcoding price IDs. `annual` /
 *  `annual_metered` are the yearly pair (Stripe needs every item on a
 *  subscription to share one interval, so the yearly plan has its own
 *  metered companion whose free tier is 12× the monthly one). */
export type PremiumPriceRole = "base" | "metered" | "annual" | "annual_metered";

/**
 * A complete Premium plan ready to enroll a user in. After the
 * usage-based migration, the plan is split across two Stripe Prices:
 *
 *   - `base`: a flat-fee recurring Price (`usage_type: "licensed"`).
 *   - `metered`: a metered recurring Price linked to the `ai_generation`
 *     Stripe Billing Meter. Tier 1 covers the included quota at 0¢/unit,
 *     tier 2 charges the overage rate.
 *
 * For backward compatibility (or for environments still on the
 * pre-migration single-Price setup) the metered slot may be null — the
 * checkout then enrolls the user in just the base Price, which is the
 * old "Premium unlocks unlimited AI" behavior.
 */
export interface PremiumPlan {
  base: PremiumPrice;
  metered: PremiumPrice | null;
  /** The yearly pair, when the catalog offers one. Anchored against the
   *  monthly price in the upsell ("$3.33 / month, billed yearly"). */
  annual: { base: PremiumPrice; metered: PremiumPrice | null } | null;
}

export type BillingInterval = "month" | "year";

/** The price pair to enroll for a chosen interval. */
export function planPricesFor(
  plan: PremiumPlan,
  interval: BillingInterval
): { base: PremiumPrice; metered: PremiumPrice | null } {
  return interval === "year" && plan.annual ? plan.annual : { base: plan.base, metered: plan.metered };
}

/** Yearly price expressed per month, in the smallest unit (for anchoring). */
export function annualPerMonthUnitAmount(annual: PremiumPrice): number {
  return Math.round(annual.unitAmount / 12);
}

/** Percentage saved by paying yearly vs 12 monthly payments, rounded. */
export function annualSavingsPercent(monthly: PremiumPrice, annual: PremiumPrice): number {
  const yearOfMonthly = monthly.unitAmount * 12;
  if (yearOfMonthly <= 0) return 0;
  return Math.max(0, Math.round((1 - annual.unitAmount / yearOfMonthly) * 100));
}

export class PremiumCheckoutUnavailableError extends Error {
  constructor() {
    super(
      "Premium checkout is unavailable: Firebase is not configured. Install the Stripe extension to enable upgrades."
    );
    this.name = "PremiumCheckoutUnavailableError";
  }
}

export class PremiumCheckoutSignInRequiredError extends Error {
  constructor() {
    super("Sign in to upgrade to Premium.");
    this.name = "PremiumCheckoutSignInRequiredError";
  }
}

export class PremiumCheckoutNoProductError extends Error {
  constructor() {
    super(
      "Premium isn't available for purchase yet. Try again later."
    );
    this.name = "PremiumCheckoutNoProductError";
  }
}

/** Resolve every active recurring price across every active Stripe product.
 *  Sorted cheapest-first. Used internally by {@link fetchPremiumPlan}.
 *  Returns an empty array when Firebase isn't configured — callers fall
 *  back to a "not configured yet" message in that case so a contributor
 *  running locally without Stripe can still see the upsell.
 *
 *  Each price's `role` field is populated from the Stripe price metadata
 *  key `role` (one of `"base"` / `"metered"`). Missing metadata becomes
 *  `null` so the legacy single-Price flow keeps working. */
export async function fetchPremiumPrices(): Promise<PremiumPrice[]> {
  const db = getFirebaseFirestore();
  if (!db) return [];

  const productsSnap = await getDocs(
    query(collection(db, "products"), where("active", "==", true))
  );

  const out: PremiumPrice[] = [];
  for (const productDoc of productsSnap.docs) {
    const product = productDoc.data() as Record<string, unknown>;
    const pricesSnap = await getDocs(
      query(collection(productDoc.ref, "prices"), where("active", "==", true))
    );
    for (const priceDoc of pricesSnap.docs) {
      const price = priceDoc.data() as Record<string, unknown>;
      const unitAmount = typeof price.unit_amount === "number" ? price.unit_amount : 0;
      // Skip one-time prices: Premium is a subscription. (The extension
      // surfaces both kinds in the same subcollection.)
      const interval =
        typeof price.interval === "string"
          ? (price.interval as PremiumPrice["interval"])
          : null;
      if (interval === null) continue;
      out.push({
        id: priceDoc.id,
        productId: productDoc.id,
        productName: typeof product.name === "string" ? product.name : "Premium",
        productDescription:
          typeof product.description === "string" ? product.description : null,
        unitAmount,
        currency:
          typeof price.currency === "string" ? price.currency : "usd",
        interval,
        trialPeriodDays:
          typeof price.trial_period_days === "number"
            ? price.trial_period_days
            : null,
        role: extractPriceRole(price),
      });
    }
  }

  out.sort((a, b) => a.unitAmount - b.unitAmount);
  return out;
}

/** Pull the `role` metadata off a Stripe Price doc synced by the
 *  invertase extension. The extension stores the price's
 *  `metadata.role` at `price.metadata.role` (string -> string map).
 *  Returns null when the field is missing, empty, or an unexpected
 *  value — the upsell flow then treats it as a legacy single-Price
 *  setup. */
function extractPriceRole(price: Record<string, unknown>): PremiumPriceRole | null {
  const metadata = price.metadata;
  if (!metadata || typeof metadata !== "object") return null;
  const role = (metadata as Record<string, unknown>).role;
  if (role === "base" || role === "metered" || role === "annual" || role === "annual_metered") return role;
  return null;
}

/**
 * Resolve the full Premium plan (base + metered) from the synced Stripe
 * catalog. Returns `null` when no active recurring Price exists (Stripe
 * isn't configured yet) — the upsell overlay handles that as a "not
 * available yet" state.
 *
 * Resolution rules:
 *   1. If exactly one base-tagged Price and one metered-tagged Price
 *      exist (the post-migration shape), pair them up and return.
 *   2. If only one untagged Price exists (legacy single-Price), return
 *      it as `{ base, metered: null }`. {@link startPremiumCheckout}
 *      then enrolls the user in just the base Price — the legacy
 *      "Premium unlocks AI without per-call billing" behavior, kept
 *      so a half-migrated Dashboard doesn't break the upsell.
 *   3. Anything else (multiple base prices, multiple metered prices, no
 *      base price) → returns `null`. The upsell overlay surfaces a
 *      "not configured" state.
 */
export async function fetchPremiumPlan(): Promise<PremiumPlan | null> {
  const prices = await fetchPremiumPrices();
  if (prices.length === 0) return null;

  const base = prices.filter((p) => p.role === "base");
  const metered = prices.filter((p) => p.role === "metered");
  const annualBase = prices.filter((p) => p.role === "annual" && p.interval === "year");
  const annualMetered = prices.filter((p) => p.role === "annual_metered" && p.interval === "year");
  // The yearly pair is optional and only offered when it is unambiguous.
  const annual =
    annualBase.length === 1
      ? { base: annualBase[0], metered: annualMetered.length === 1 ? annualMetered[0] : null }
      : null;

  if (base.length === 1 && metered.length === 1) {
    return { base: base[0], metered: metered[0], annual };
  }
  if (base.length === 1 && metered.length === 0) {
    // Half-migrated: metered side isn't tagged yet. The upsell can
    // still render, but checkout won't bill overage. Operator should
    // tag the metered Price as soon as possible.
    return { base: base[0], metered: null, annual };
  }
  if (base.length === 0 && metered.length === 0 && prices.length === 1) {
    // Legacy pre-migration setup — one un-tagged recurring Price.
    return { base: prices[0], metered: null, annual: null };
  }
  // Ambiguous catalog (multiple base prices, multiple metered, etc.).
  // Don't guess; force the operator to tag prices correctly.
  console.warn(
    "[stripeCheckout] cannot resolve Premium plan from catalog: " +
      `base=${base.length}, metered=${metered.length}, total=${prices.length}. ` +
      "Tag exactly one Price with metadata role='base' and one with role='metered'."
  );
  return null;
}

export interface StartCheckoutOptions {
  /**
   * The base recurring Price ID (`usage_type: "licensed"`). Required.
   * This is the flat fee subscription line item and takes `quantity: 1`.
   */
  priceId: string;
  /**
   * Optional metered overage Price ID (`usage_type: "metered"`). When
   * provided, the checkout session enrolls the user in both Prices on a
   * single Subscription. Stripe forbids `quantity` on metered line items
   * (it's derived from meter usage), so this is wired into `line_items`
   * with the `quantity` field intentionally omitted — sending it raises
   * "Quantity should not be specified where usage_type is `metered`".
   */
  meteredPriceId?: string;
  /** Where Stripe sends the user after a successful purchase. Defaults to
   *  the current URL with `?checkout=success` appended — the App effect
   *  consumes that param and force-refreshes the ID token so the new
   *  `stripeRole` claim is visible immediately. */
  successUrl?: string;
  cancelUrl?: string;
  /** Show the promo-code input on the Stripe Checkout page. Defaults to
   *  true since the upside (occasional discount) is high and the cost is
   *  zero when no promo exists. */
  allowPromotionCodes?: boolean;
  trialPeriodDays?: number;
  /** Price + feature context for the `begin_checkout` analytics event. */
  analytics?: { base: PremiumPrice | null; feature: PremiumFeatureKey | null };
}

/** Stripe currencies with no minor unit; `unit_amount` is already whole. */
const ZERO_DECIMAL_CURRENCIES = new Set([
  "bif", "clp", "djf", "gnf", "jpy", "kmf", "krw", "mga", "pyg", "rwf",
  "ugx", "vnd", "vuv", "xaf", "xof", "xpf",
]);

/** Stripe `unit_amount` → major units (GA4 / Meta `value`). */
export function priceToMajorUnits(unitAmount: number, currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase())
    ? unitAmount
    : Math.round(unitAmount) / 100;
}

/**
 * Kick off Stripe Checkout for a Premium plan and redirect the browser
 * once the extension has provisioned the session URL.
 *
 * Supports both the legacy single-Price setup and the post-migration
 * two-Price setup (base + metered overage). Pass `priceId` alone for the
 * former or pass both `priceId` and `meteredPriceId` for the latter.
 * When the metered Price is provided, the extension's `line_items`
 * code path activates and the resulting Subscription has both items —
 * the base flat fee (`quantity: 1`) and the metered overage attached to
 * the `ai_generation` meter (no `quantity`, per Stripe's metered-price
 * contract).
 *
 * Resolves only after `window.location.assign` is called (which navigates
 * the page away — the promise effectively never settles in practice).
 * Rejects when the extension writes an error back to the session doc, or
 * when the caller isn't signed in / Firebase isn't configured.
 */
export async function startPremiumCheckout(
  opts: StartCheckoutOptions
): Promise<void> {
  const db = getFirebaseFirestore();
  if (!db) throw new PremiumCheckoutUnavailableError();
  const user = getCurrentUser();
  if (!user) throw new PremiumCheckoutSignInRequiredError();

  const successUrl = opts.successUrl ?? defaultReturnUrl("success");
  const cancelUrl = opts.cancelUrl ?? defaultReturnUrl("cancel");

  const sessionDoc: Record<string, unknown> = {
    success_url: successUrl,
    cancel_url: cancelUrl,
    allow_promotion_codes: opts.allowPromotionCodes ?? true,
  };
  if (!opts.meteredPriceId) {
    // Single-price path keeps the simpler `price` field so older
    // extension versions (pre-line_items) still work. See POSTINSTALL.md
    // (https://github.com/stripe/stripe-firebase-extensions/blob/next/firestore-stripe-payments/POSTINSTALL.md).
    sessionDoc.price = opts.priceId;
  } else {
    // Two-price (base + metered) path. The metered line item must NOT
    // carry `quantity` — Stripe rejects the session otherwise with
    // "Quantity should not be specified where usage_type is `metered`".
    sessionDoc.line_items = [
      { price: opts.priceId, quantity: 1 },
      { price: opts.meteredPriceId },
    ];
  }
  if (opts.trialPeriodDays) {
    sessionDoc.trial_period_days = opts.trialPeriodDays;
  }

  const ref = await addDoc(
    collection(db, "customers", user.uid, "checkout_sessions"),
    sessionDoc
  );

  const base = opts.analytics?.base ?? null;
  track("begin_checkout", {
    value: base ? priceToMajorUnits(base.unitAmount, base.currency) : null,
    currency: base ? base.currency.toUpperCase() : null,
    plan_interval: base?.interval ?? null,
    trial_days: opts.trialPeriodDays ?? base?.trialPeriodDays ?? null,
    has_metered_price: Boolean(opts.meteredPriceId),
    feature: opts.analytics?.feature ?? null,
  });

  return new Promise<void>((resolve, reject) => {
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.data() as
          | { url?: string; error?: { message?: string } | null }
          | undefined;
        if (!data) return;
        if (data.error) {
          unsub();
          const err = new Error(
            data.error.message ?? "Couldn't start Stripe Checkout."
          );
          captureException(err, { context: "startPremiumCheckout.extensionError" });
          reject(err);
          return;
        }
        if (data.url) {
          unsub();
          if (typeof window !== "undefined") {
            window.location.assign(data.url);
          }
          resolve();
        }
      },
      (err) => {
        unsub();
        captureException(err, { context: "startPremiumCheckout.snapshot" });
        reject(err);
      }
    );
  });
}

function defaultReturnUrl(kind: "success" | "cancel"): string {
  if (typeof window === "undefined") return "/";
  const url = new URL(window.location.href);
  url.searchParams.set("checkout", kind);
  return url.toString();
}

/**
 * Open the Stripe Customer Portal in the same tab so the user can update
 * their payment method. Cancellation itself goes through
 * {@link cancelPremiumSubscription} so it always waits until the period
 * ends. The extension's `createPortalLink` callable returns a single-use URL.
 */
export async function openCustomerPortal(returnUrl?: string): Promise<void> {
  const app = getFirebaseApp();
  if (!app) throw new PremiumCheckoutUnavailableError();
  const user = getCurrentUser();
  if (!user) throw new PremiumCheckoutSignInRequiredError();

  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fns = getFunctions(app, getFunctionsRegion());
  const callable = httpsCallable<{ returnUrl?: string }, { url: string }>(
    fns,
    PORTAL_CALLABLE_NAME
  );
  const target =
    returnUrl ?? (typeof window !== "undefined" ? window.location.href : "/");
  const result = await callable({ returnUrl: target });
  if (result.data?.url && typeof window !== "undefined") {
    window.location.assign(result.data.url);
  }
}

/**
 * Schedule the signed-in user's Premium subscription to cancel at the
 * end of the current billing period, or undo that. The Cloud Function
 * looks up the subscription itself — callers never pass a Stripe id.
 */
export async function cancelPremiumSubscription(
  action: CancelSubscriptionAction
): Promise<CancelSubscriptionResult> {
  const app = getFirebaseApp();
  if (!app) throw new PremiumCheckoutUnavailableError();
  const user = getCurrentUser();
  if (!user) throw new PremiumCheckoutSignInRequiredError();

  const { getFunctions, httpsCallable } = await import("firebase/functions");
  const fns = getFunctions(app, getFunctionsRegion());
  const callable = httpsCallable<
    { action: CancelSubscriptionAction },
    CancelSubscriptionResult
  >(fns, CANCEL_CALLABLE_NAME);
  const result = await callable({ action });
  return result.data;
}

/** Coerce a Stripe unix timestamp or a Firestore Timestamp into seconds.
 *  Kept in lockstep with `readUnixSeconds` in
 *  `functions/src/billing/cancelSubscription.ts`. */
export function readUnixSeconds(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value > 1e11 ? Math.floor(value / 1000) : Math.floor(value);
  }
  if (value && typeof value === "object") {
    const stamped = value as { toDate?: () => Date; seconds?: unknown };
    if (typeof stamped.toDate === "function") {
      const ms = stamped.toDate().getTime();
      if (Number.isFinite(ms)) return Math.floor(ms / 1000);
    }
    if (typeof stamped.seconds === "number" && Number.isFinite(stamped.seconds)) {
      return Math.floor(stamped.seconds);
    }
  }
  return null;
}

/** Period end from a subscription doc the Stripe extension synced.
 *  Item-level `current_period_end` is the current Stripe shape;
 *  a top-level field covers older payloads. */
export function parseSubscriptionPeriodEnd(data: {
  current_period_end?: unknown;
  cancel_at?: unknown;
  cancel_at_period_end?: unknown;
  items?: unknown;
}): number | null {
  const top = readUnixSeconds(data.current_period_end);
  if (top != null) return top;

  const items = data.items;
  const list = Array.isArray(items)
    ? items
    : items &&
        typeof items === "object" &&
        Array.isArray((items as { data?: unknown }).data)
      ? (items as { data: unknown[] }).data
      : [];
  const itemEnds: number[] = [];
  for (const item of list) {
    if (!item || typeof item !== "object") continue;
    const end = readUnixSeconds(
      (item as { current_period_end?: unknown }).current_period_end
    );
    if (end != null) itemEnds.push(end);
  }
  if (itemEnds.length > 0) return Math.max(...itemEnds);

  if (data.cancel_at_period_end === true) {
    return readUnixSeconds(data.cancel_at);
  }
  return null;
}

function manageableSubscriptionFromDoc(
  id: string,
  data: Record<string, unknown>
): ManageableSubscription | null {
  const status = data.status;
  if (
    status !== "active" &&
    status !== "trialing" &&
    status !== "past_due"
  ) {
    return null;
  }
  return {
    id,
    status,
    cancelAtPeriodEnd: data.cancel_at_period_end === true,
    currentPeriodEnd: parseSubscriptionPeriodEnd(data),
  };
}

/**
 * Live view of the signed-in user's subscriptions the Customer Portal
 * (and our cancel callable) can act on. Fires once immediately, then
 * on every change. Calls `onChange([])` when Firestore isn't configured
 * or the listener errors, so the Settings section stays hidden for
 * Premium-override-only sessions.
 */
export function subscribeManageableSubscriptions(
  uid: string,
  onChange: (subs: ManageableSubscription[]) => void
): () => void {
  const db = getFirebaseFirestore();
  if (!db) {
    onChange([]);
    return () => {};
  }

  return onSnapshot(
    query(
      collection(db, "customers", uid, "subscriptions"),
      where("status", "in", [...MANAGEABLE_SUBSCRIPTION_STATUSES])
    ),
    (snap) => {
      const subs: ManageableSubscription[] = [];
      for (const doc of snap.docs) {
        const parsed = manageableSubscriptionFromDoc(
          doc.id,
          doc.data() as Record<string, unknown>
        );
        if (parsed) subs.push(parsed);
      }
      onChange(subs);
    },
    () => {
      onChange([]);
    }
  );
}

/**
 * Subscribe to the user's `customers/{uid}/subscriptions` collection and
 * fire `onActive` the first time a subscription doc with status
 * `"active"` or `"trialing"` appears.
 *
 * Why this exists: the `invertase/firestore-stripe-payments` extension's
 * webhook handler writes the subscription doc and stamps the
 * `stripeRole` custom claim in the same code path. The Firestore write
 * is observable in real time over `onSnapshot`, so listening here gives
 * us a sub-second signal that the claim has just landed — much faster
 * than the worst-case ~13s gap in {@link import("./premium").pollPremiumUntilActive}'s
 * backoff schedule. The caller (`App.tsx`) wires this signal into
 * `refreshPremium()` to immediately re-read the claim out of the ID
 * token.
 *
 * Contract:
 *   - `onActive` fires AT MOST ONCE per call, guarded by a local flag
 *     so churn on the snapshot (e.g. status change `trialing` →
 *     `active`) doesn't re-trigger the post-checkout refresh.
 *   - Returns an unsubscribe function. Calling it is idempotent.
 *   - If `opts.signal` aborts (already aborted at call time or aborts
 *     later), the subscription tears down and `onActive` will not fire.
 *   - No-ops (returns a no-op unsubscribe) when Firestore isn't
 *     configured, matching {@link fetchPremiumPrices} /
 *     {@link hasManageableSubscription}.
 */
export function subscribeActiveStripeSubscription(
  uid: string,
  onActive: () => void,
  opts: { signal?: AbortSignal } = {}
): () => void {
  const db = getFirebaseFirestore();
  if (!db) return () => {};

  const { signal } = opts;
  if (signal?.aborted) return () => {};

  let fired = false;
  let unsubFromSnapshot: (() => void) | null = null;
  let torn = false;

  function teardown() {
    if (torn) return;
    torn = true;
    if (unsubFromSnapshot) {
      unsubFromSnapshot();
      unsubFromSnapshot = null;
    }
    signal?.removeEventListener("abort", teardown);
  }

  signal?.addEventListener("abort", teardown, { once: true });

  unsubFromSnapshot = onSnapshot(
    query(
      collection(db, "customers", uid, "subscriptions"),
      where("status", "in", ["trialing", "active"])
    ),
    (snap) => {
      if (fired || torn) return;
      if (snap.empty) return;
      fired = true;
      try {
        onActive();
      } finally {
        // Once we've signalled the caller there's nothing more to watch
        // for in this lifecycle — tear down to drop the listener cost.
        teardown();
      }
    },
    () => {
      // Permission denied / network error / customer doc doesn't exist
      // yet. Silently swallow — the polling fallback in premium.ts will
      // still catch the claim once the extension finishes.
      teardown();
    }
  );

  return teardown;
}

/** True iff the signed-in user has any subscription in a state that the
 *  Customer Portal can act on. Used by the upsell overlay to swap the
 *  primary CTA between "Upgrade to Premium" and "Manage subscription". */
export async function hasManageableSubscription(): Promise<boolean> {
  const db = getFirebaseFirestore();
  if (!db) return false;
  const user = getCurrentUser();
  if (!user) return false;
  try {
    const snap = await getDocs(
      query(
        collection(db, "customers", user.uid, "subscriptions"),
        where("status", "in", ["trialing", "active", "past_due"])
      )
    );
    return !snap.empty;
  } catch {
    // The customer doc may not exist yet (extension creates it lazily on
    // first checkout). Treat as "no manageable subscription".
    return false;
  }
}

/** Strip the `?checkout=success` query param from the URL after a Stripe
 *  Checkout redirect. Returns `true` if the param was present so the
 *  caller can decide whether to force-refresh the premium claim. */
export function consumeCheckoutSuccessParam(): boolean {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  const flag = url.searchParams.get("checkout");
  if (flag !== "success") return false;
  url.searchParams.delete("checkout");
  window.history.replaceState({}, "", url.toString());
  return true;
}

/** Strip `?checkout=cancel` (the Stripe Checkout back button) from the
 *  URL. Returns `true` if it was present. */
export function consumeCheckoutCancelParam(): boolean {
  if (typeof window === "undefined") return false;
  const url = new URL(window.location.href);
  if (url.searchParams.get("checkout") !== "cancel") return false;
  url.searchParams.delete("checkout");
  window.history.replaceState({}, "", url.toString());
  return true;
}

/** Read-only peek at whether `?checkout=success` is currently in the
 *  URL. Used to seed React state at render time without the side effect
 *  of stripping the param — the effect-time `consumeCheckoutSuccessParam`
 *  does that on the same tick. Safe to call during render. */
export function hasCheckoutSuccessParam(): boolean {
  if (typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("checkout") === "success"
  );
}

/** Format `unitAmount` in `currency` as a human price string. Falls back
 *  to a manual formatter when the runtime's `Intl` doesn't know the
 *  currency (e.g. a misconfigured Stripe product). */
export function formatPrice(unitAmount: number, currency: string): string {
  const code = (currency || "usd").toUpperCase();
  const dollars = unitAmount / 100;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: code,
      minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
    }).format(dollars);
  } catch {
    return `${code} ${dollars.toFixed(2)}`;
  }
}

/** Short interval label for the price line (e.g. "/ month", "/ year"). */
export function formatInterval(interval: PremiumPrice["interval"]): string {
  switch (interval) {
    case "month":
      return "/ month";
    case "year":
      return "/ year";
    case "week":
      return "/ week";
    case "day":
      return "/ day";
    case null:
    default:
      return "";
  }
}
